import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Globe,
  Network,
  Mail,
  Fingerprint,
  User,
  Terminal,
  BookOpen,
  Shield,
  Hash,
  Code2,
  Image as ImageIcon,
  Wrench,
  Eye,
  Lock,
  Zap,
  Activity,
  Radio,
  Cpu,
  ChevronRight,
  GraduationCap,
} from "lucide-react";
import {
  ScanSearch,
  Radar,
  AtSign,
  MailSearch,
  UserSearch,
  Binary,
  FileDigit,
  ScanLine,
  SquareTerminal,
} from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { InteractiveConsole } from "@/components/InteractiveConsole";
import { MatrixEffect } from "@/components/MatrixEffect";
import { PromotionBanner } from "@/components/PromotionBanner";
import { DeferUntilVisible } from "@/components/DeferUntilVisible";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import {
  IntelCard,
  SectionEyebrow,
  SectionTitle,
  MetricCell,
  SeverityBadge,
  LiveTicker,
  StatusRail,
  TerminalChrome,
  Heatmap,
  IntelButton,
} from "@/components/intel";
const CVEIntelligence = lazy(() =>
  import("@/components/intel/CVEIntelligence").then((m) => ({ default: m.CVEIntelligence })),
);
import { SEOHead, OrganizationSchema, WebApplicationSchema, WebSiteSchema } from "@/components/seo";

/* =================================================================
 * LOWKEYX // INTELLIGENCE CONSOLE v2 — Landing
 * Editorial bands · high-density technical layout.
 * ================================================================= */

const PUBLIC_TOOLS = ["domain_recon", "ip_intelligence"];

const osintTools = [
  { id: "domain_recon",    icon: ScanSearch,  code: "DOM_01", name: "Domain Recon",     desc: "WHOIS · DNS · SSL · Subdominios",        free: true,  tags: ["dns","whois","ssl"], accent: "primary" as const },
  { id: "ip_intelligence", icon: Radar,       code: "NET_02", name: "IP Intelligence",  desc: "Geo · ASN · Reputación · VPN/Proxy",     free: true,  tags: ["asn","geo","vpn"],  accent: "cyan"    as const },
  { id: "email_recon",     icon: AtSign,      code: "EML_03", name: "Email Recon",      desc: "Deep OSINT · Breaches · Plataformas",    free: false, tags: ["breach","osint"],   accent: "amber"   as const },
  { id: "email_hunter",    icon: MailSearch,  code: "EML_04", name: "Email Hunter",     desc: "Emails corporativos · Patrones",         free: false, tags: ["corp","pattern"],   accent: "primary" as const },
  { id: "username_search", icon: UserSearch,  code: "USR_05", name: "Username Search",  desc: "150+ plataformas · Validación HTTP",     free: false, tags: ["social","150+"],    accent: "cyan"    as const },
];

const utilityTools = [
  { icon: Binary,         code: "ENC_01", name: "Encoders",    desc: "Base64 · URL · HTML · JWT",  accent: "primary" as const },
  { icon: FileDigit,      code: "HSH_02", name: "Hash Lab",    desc: "MD5 · SHA · bcrypt · CRC32", accent: "amber"   as const },
  { icon: ScanLine,       code: "IMG_03", name: "Image OSINT", desc: "EXIF · Reverse · Metadata",  accent: "cyan"    as const },
  { icon: SquareTerminal, code: "TRM_04", name: "Terminal",    desc: "CLI con autocompletado",     accent: "primary" as const },
];

const intelFeed = [
  { sev: "critical" as const, id: "CVE-2025-31904", desc: "Remote code execution en orquestador de contenedores ampliamente desplegado.", tags: ["rce","container","unauth"], score: "9.8" },
  { sev: "high"     as const, id: "CVE-2025-31817", desc: "Escalado de privilegios local en kernel Linux ≤ 6.12.", tags: ["lpe","linux","kernel"], score: "8.7" },
  { sev: "high"     as const, id: "CVE-2025-31750", desc: "Bypass de autenticación en plugin SSO empresarial.", tags: ["auth-bypass","saml"], score: "8.4" },
  { sev: "medium"   as const, id: "CVE-2025-31702", desc: "SSRF a través de webhook en plataforma de CI/CD popular.", tags: ["ssrf","ci"], score: "6.9" },
  { sev: "info"     as const, id: "CVE-2025-31699", desc: "Information disclosure en endpoint de métricas no protegido.", tags: ["info-leak","metrics"], score: "5.3" },
];

const tickerItems = [
  { tone: "err"  as const, text: "● CVE-2025-31904 · CVSS 9.8 · RCE container orchestrator" },
  { tone: "warn" as const, text: "▲ Phishing wave detected · banking vertical · ES/PT" },
  { tone: "ok"   as const, text: "✓ All OSINT modules operational · latency < 800ms" },
  { tone: "info" as const, text: "ℹ New writeup published · HTB · Active Directory" },
  { tone: "err"  as const, text: "● CVE-2025-31817 · CVSS 8.7 · Linux kernel LPE" },
  { tone: "warn" as const, text: "▲ Suspicious VPN exit nodes flagged · 12 ASNs" },
  { tone: "ok"   as const, text: "✓ Knowledge base sync · 120+ articles indexed" },
];

const tracks = [
  {
    key: "operator",
    band: "OPERATOR",
    color: "text-primary",
    title: "Pista técnica",
    desc: "Para analistas, pentesters y curiosos con base. Reconocimiento, OSINT, simulaciones y writeups.",
    items: ["Reconocimiento OSINT en profundidad", "Terminal avanzada con autocompletado", "Writeups y vulnerabilidades", "Práctica con módulos reales"],
    cta: { label: "Acceder a Tools", to: "/arsenal" },
  },
  {
    key: "awareness",
    band: "AWARENESS",
    color: "text-signal-cyan",
    title: "Pista para personas",
    desc: "Para usuarios sin conocimiento técnico. Aprende a defenderte de phishing, fraudes y suplantaciones con simulaciones reales.",
    items: ["Simuladores de phishing y webs falsas", "Diccionario vivo de términos", "Retos guiados por nivel", "Lenguaje claro, sin jerga"],
    cta: { label: "Entrar a Conciencia", to: "/conciencia" },
  },
];

const Index = () => {
  const [matrixMode, setMatrixMode] = useState(false);
  const [showConsole, setShowConsole] = useState(false);
  const [showLandingPromo, setShowLandingPromo] = useState(false);
  const [showHeroConsole, setShowHeroConsole] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const consoleTimer = window.setTimeout(() => setShowHeroConsole(true), 350);
    const promoTimer = window.setTimeout(() => setShowLandingPromo(true), 900);

    return () => {
      clearTimeout(consoleTimer);
      clearTimeout(promoTimer);
    };
  }, []);

  const handleToolClick = (e: React.MouseEvent, toolId: string) => {
    if (!user && !PUBLIC_TOOLS.includes(toolId)) {
      e.preventDefault();
      navigate("/auth");
    }
  };

  const handleConsoleEasterEgg = (type: string) => {
    if (type === "matrix") setMatrixMode(true);
  };

  // Deterministic heatmap data so SSR/CSR match
  const heatmap = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < 14 * 7; i++) {
      const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
      arr.push(Math.floor(Math.abs(x - Math.floor(x)) * 6));
    }
    return arr;
  }, []);

  return (
    <AppLayout>
      <SEOHead
        title="Plataforma OSINT y Conciencia Digital"
        description="Herramientas OSINT, terminal avanzada, academia de ciberseguridad y simuladores de conciencia digital para todos los niveles."
        canonical="/"
        keywords={["OSINT", "ciberseguridad", "hacking", "concienciación digital", "phishing", "herramientas OSINT"]}
      />
      <OrganizationSchema />
      <WebApplicationSchema />
      <WebSiteSchema />

      {matrixMode
        ? createPortal(
            <MatrixEffect active={matrixMode} onComplete={() => setMatrixMode(false)} />,
            document.body,
          )
        : null}

      {/* ============================================================
          BAND 00 · STATUS RAIL
      ============================================================ */}
      <StatusRail
        left={
          <>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-signal-green intel-pulse-green" />
            <span className="hidden sm:inline">PLATFORM ONLINE</span>
            <span className="hidden md:inline text-border">|</span>
            <span className="hidden md:inline">ENV · PRODUCTION</span>
            <span className="hidden lg:inline text-border">|</span>
            <span className="hidden lg:inline">BUILD · v2.0</span>
          </>
        }
        right={
          <>
            <LiveClock />
          </>
        }
      />

      {showLandingPromo ? (
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-3">
          <PromotionBanner placement="landing" />
        </div>
      ) : null}

      {/* ============================================================
          BAND 01 · HERO
      ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 lg:pt-12 pb-16 lg:pb-24 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="intel-eyebrow text-primary">
                <span>INTELLIGENCE RESEARCH PLATFORM</span>
              </div>

              <h1 className="intel-display mt-7 text-foreground text-[clamp(2.6rem,8vw,6.5rem)]">
                <span className="block">VE</span>
                <span className="block">
                  <span className="text-primary">LO QUE</span>
                  <span className="text-muted-foreground/40"> OTROS</span>
                </span>
                <span className="block">NO VEN.</span>
              </h1>

              <p className="mt-8 max-w-xl text-base sm:text-[15px] text-muted-foreground leading-[1.8]">
                Plataforma seria de hacking técnico y conciencia digital.
                Reconocimiento OSINT, terminal avanzada, academia y simulaciones reales —
                para operadores y para personas sin conocimiento previo.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/arsenal">
                  <IntelButton variant="primary">
                    Iniciar operación
                    <ArrowRight className="w-3.5 h-3.5" />
                  </IntelButton>
                </Link>
                <Link to="/conciencia">
                  <IntelButton variant="ghost">
                    Conciencia digital
                  </IntelButton>
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
                <Stat icon={Eye}  label="Sin rastros para los objetivos" />
                <Stat icon={Lock} label="Consultas anónimas privadas" />
                <Stat icon={Zap}  label="Latencia media < 800ms" />
              </div>
            </div>

            {/* Right: console + metric cells */}
            <div className="lg:col-span-5 space-y-3">
              <TerminalChrome
                title="lowkeyx@operator ~ ─ interactive console"
                footer={
                  <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-1 h-1 rounded-full bg-signal-green intel-pulse-green" />
                      session · ready
                    </span>
                    <span>type <span className="text-primary">help</span></span>
                  </div>
                }
              >
                <div className="hidden sm:block">
                  {showHeroConsole ? (
                    <InteractiveConsole onEasterEgg={handleConsoleEasterEgg} />
                  ) : (
                    <div className="h-40 sm:h-52 lg:h-60 overflow-hidden font-mono text-[11px] sm:text-xs md:text-sm p-3 text-muted-foreground bg-gradient-to-b from-[hsl(0,5%,7%)] to-[hsl(0,5%,4%)]">
                      <div>&gt; LOWKEYX Terminal [Demo]</div>
                      <div>&gt; Inicializando consola…</div>
                      <div className="mt-3 text-foreground/70">$ help</div>
                      <div className="mt-1 inline-block text-primary animate-blink-caret">▌</div>
                    </div>
                  )}
                </div>
                <div className="sm:hidden">
                  {!showConsole ? (
                    <button
                      onClick={() => setShowConsole(true)}
                      className="w-full p-5 text-left group"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 border border-primary/40 bg-primary/10 flex items-center justify-center">
                          <Terminal className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">Tap para abrir</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Comandos OSINT en tiempo real</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-active:translate-x-1 transition-transform" />
                      </div>
                      <div className="border border-border bg-surface-0 p-3 font-mono text-xs">
                        <span className="text-signal-green">$</span>{" "}
                        <span className="text-foreground/70">help</span>
                        <span className="ml-1 animate-blink-caret text-primary">▌</span>
                      </div>
                    </button>
                  ) : (
                    <InteractiveConsole onEasterEgg={handleConsoleEasterEgg} />
                  )}
                </div>
              </TerminalChrome>

              <div className="grid grid-cols-2 gap-px bg-border">
                <MetricCell label="Módulos OSINT"      value="08" unit="+" hint="↑ growing" />
                <MetricCell label="Plataformas user"   value="150" unit="+" hint="HTTP validated" />
                <MetricCell label="Writeups"           value="120" unit="+" hint="curated" />
                <MetricCell label="Simuladores"        value="06"           hint="awareness" />
              </div>
            </div>
          </div>
        </div>

        {/* Live ticker bottom of hero */}
        <LiveTicker items={tickerItems} />
      </section>

      {/* ============================================================
          BAND 02 · INTELLIGENCE FEED + HEATMAP
      ============================================================ */}
      <section className="bg-surface-1/40 border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <SectionEyebrow index="02">VULN INTELLIGENCE</SectionEyebrow>
          <div className="flex items-end justify-between flex-wrap gap-4 mt-3 mb-12">
            <SectionTitle>
              CVE FEED <span className="text-muted-foreground/40">/</span> ACTIVITY MAP
            </SectionTitle>
            <Link to="/knowledge" className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary hover:text-[hsl(var(--primary-glow))] flex items-center gap-2">
              VER KNOWLEDGE <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <DeferUntilVisible
            minHeight={520}
            fallback={
              <div className="grid lg:grid-cols-[1fr_360px] gap-px bg-border">
                <div className="bg-surface-2 p-5 min-h-[480px] font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  // cargando feed CVE…
                </div>
                <div className="bg-surface-2 p-5 min-h-[200px]" />
              </div>
            }
          >
          <div className="grid lg:grid-cols-[1fr_360px] gap-px bg-border">
            <Suspense fallback={<div className="bg-surface-2 p-5 min-h-[480px]" />}>
              <CVEIntelligence />
            </Suspense>

            {/* Heatmap + attack surface */}
            <div className="space-y-px">
              <div className="bg-surface-2 p-5">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-4">// 7d · GLOBAL ACTIVITY</h3>
                <Heatmap cols={14} rows={7} data={heatmap} />
                <div className="flex items-center justify-between mt-4 font-mono text-[8px] uppercase tracking-[0.14em] text-muted-foreground">
                  <span>LOW</span>
                  <div className="flex gap-[2px]">
                    {[0,1,2,3,4,5].map((i) => (
                      <span key={i} className={cn("block w-2.5 h-2.5", ["bg-surface-3","bg-primary/15","bg-primary/30","bg-primary/50","bg-primary/75","bg-primary"][i])} />
                    ))}
                  </div>
                  <span>HIGH</span>
                </div>
              </div>

              <div className="bg-surface-2 p-5">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-4">// ATTACK SURFACE</h3>
                <div className="space-y-3">
                  {[
                    { label: "Phishing campaigns",  value: 87, tone: "bg-primary" },
                    { label: "Credential stuffing", value: 64, tone: "bg-orange-500" },
                    { label: "Unpatched CVEs",      value: 49, tone: "bg-signal-amber" },
                    { label: "Exposed services",    value: 31, tone: "bg-signal-cyan" },
                  ].map((row) => (
                    <div key={row.label}>
                      <div className="flex items-center justify-between font-mono text-[9.5px] uppercase tracking-[0.14em] mb-1.5">
                        <span className="text-muted-foreground">{row.label}</span>
                        <span className="text-foreground tabular-nums">{row.value}%</span>
                      </div>
                      <div className="h-[3px] bg-surface-3 overflow-hidden">
                        <div className={cn("h-full", row.tone)} style={{ width: `${row.value}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </DeferUntilVisible>
        </div>
      </section>

      {/* ============================================================
          BAND 03 · OSINT MODULES
      ============================================================ */}
      <section className="border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <SectionEyebrow index="03">OSINT MODULES</SectionEyebrow>
          <div className="flex items-end justify-between flex-wrap gap-4 mt-3 mb-12">
            <SectionTitle>
              ARSENAL <span className="text-muted-foreground/40">·</span> RECON
            </SectionTitle>
            <Link to="/arsenal" className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary hover:text-[hsl(var(--primary-glow))] flex items-center gap-2">
              VER TODO <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {osintTools.map((t, idx) => (
              <Link
                key={t.id}
                to={`/arsenal?tool=${t.id}`}
                onClick={(e) => handleToolClick(e, t.id)}
                className="group block focus:outline-none"
              >
                <ToolBox
                  index={idx + 1}
                  code={t.code}
                  name={t.name}
                  desc={t.desc}
                  Icon={t.icon}
                  accent={t.accent}
                  status={t.free ? "FREE" : !user ? "AUTH" : "READY"}
                  statusTone={t.free ? "ok" : !user ? "warn" : "info"}
                  tags={t.tags}
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          BAND 04 · TWO TRACKS
      ============================================================ */}
      <section className="bg-surface-1/40 border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <SectionEyebrow index="04">DOS PISTAS · UN OBJETIVO</SectionEyebrow>
          <SectionTitle>
            PARA OPERADORES{" "}
            <span className="text-muted-foreground/40">/</span>{" "}
            <span className="text-signal-cyan">PARA PERSONAS</span>
          </SectionTitle>
          <p className="mt-6 max-w-2xl text-[14.5px] text-muted-foreground leading-[1.8]">
            La plataforma se divide en dos pistas claramente separadas: técnica para quien sabe,
            y didáctica para quien no — sin condescendencia, con simuladores reales.
          </p>

          <div className="mt-12 grid lg:grid-cols-2 gap-px bg-border">
            {tracks.map((tr, i) => (
              <IntelCard
                key={tr.key}
                moduleId={`TRACK_${String(i + 1).padStart(2, "0")} · ${tr.band}`}
                status={i === 0 ? "live" : "ok"}
                meta={<span className={tr.color}>{i === 0 ? "TECH" : "PEOPLE"}</span>}
                className="h-full"
              >
                <h3 className={cn("intel-display text-[1.6rem] mt-1", tr.color)}>{tr.title}</h3>
                <p className="text-[14px] text-muted-foreground leading-[1.7] mt-4 mb-5">{tr.desc}</p>
                <ul className="space-y-2.5 mb-7">
                  {tr.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[13.5px] text-foreground/85">
                      <span className={cn("font-mono text-[11px] mt-1", tr.color)}>›</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <Link to={tr.cta.to}>
                  <IntelButton variant={i === 0 ? "primary" : "outline"}>
                    {tr.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                  </IntelButton>
                </Link>
              </IntelCard>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          BAND 05 · UTILITIES (compact rail)
      ============================================================ */}
      <section className="border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <SectionEyebrow index="05">UTILIDADES</SectionEyebrow>
          <div className="flex items-end justify-between flex-wrap gap-4 mt-3 mb-12">
            <SectionTitle>CAJA DE HERRAMIENTAS</SectionTitle>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              encoders · hashing · forensics · cli
            </span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {utilityTools.map((u, idx) => (
              <Link key={u.code} to="/arsenal" className="group block">
                <UtilityBox
                  index={idx + 1}
                  code={u.code}
                  name={u.name}
                  desc={u.desc}
                  Icon={u.icon}
                  accent={u.accent}
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          BAND 06 · WHY (split with rail)
      ============================================================ */}
      <section className="bg-surface-1/40 border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionEyebrow index="06">POR QUÉ LOWKEYX</SectionEyebrow>
              <SectionTitle>
                NO ES UNA{" "}
                <span className="text-muted-foreground/40">LANDING.</span>
                <br />
                ES UNA <span className="text-primary">CONSOLA.</span>
              </SectionTitle>
              <p className="mt-6 text-[14.5px] text-muted-foreground leading-[1.8]">
                Discreción, velocidad, claridad. Diseñada como una herramienta operativa real:
                cada módulo tiene ID, estado, latencia y resultado. Sin adornos vacíos.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-border">
              {[
                { icon: Eye,  title: "Sin rastros",          desc: "No notificamos a objetivos. No registramos rutas a terceros. Las consultas anónimas no generan logs personales." },
                { icon: Cpu,  title: "Densidad informativa", desc: "Cada panel está pensado como módulo operativo: ID, estado, latencia, resultado. Nada decorativo." },
                { icon: Radio, title: "Realmente didáctica", desc: "La pista de Conciencia usa simuladores reales (phishing, webs falsas, mensajes) para personas sin base técnica." },
                { icon: Shield, title: "Privacidad por diseño", desc: "Auth con OTP, sesiones limpias, anonimato preservado. Sin telemetría agresiva." },
              ].map((f) => (
                <div key={f.title} className="bg-surface-2 p-6 hover:bg-surface-3 transition-colors">
                  <f.icon className="w-5 h-5 text-primary mb-4" strokeWidth={1.6} />
                  <h4 className="font-display text-[15px] font-bold tracking-wide mb-2">{f.title}</h4>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          BAND 07 · CTA
      ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-32">
          <div className="relative bg-surface-2 border border-border p-8 sm:p-14 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent" />
            <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-primary/10 blur-[120px] pointer-events-none" />

            <div className="relative grid md:grid-cols-3 gap-10 items-center">
              <div className="md:col-span-2">
                <SectionEyebrow index="07">ENTRAR</SectionEyebrow>
                <h2 className="intel-display text-foreground text-[clamp(1.8rem,4.5vw,3.5rem)] mt-3">
                  EMPIEZA AHORA. <span className="text-primary">SIN BARRERAS.</span>
                </h2>
                <p className="mt-5 text-[14.5px] text-muted-foreground max-w-xl leading-[1.8]">
                  Dos herramientas OSINT son completamente gratuitas y públicas.
                  Para el resto, regístrate en menos de 30 segundos.
                </p>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                {!user && (
                  <Link to="/auth">
                    <IntelButton variant="primary" className="w-full md:w-auto h-12 px-7">
                      <GraduationCap className="w-4 h-4" /> CREAR CUENTA
                      <ArrowRight className="w-4 h-4" />
                    </IntelButton>
                  </Link>
                )}
                <Link to="/arsenal">
                  <IntelButton variant="outline" className="w-full md:w-auto h-12 px-7">
                    <Wrench className="w-4 h-4" /> EXPLORAR TOOLS
                  </IntelButton>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

/* ----------------------- Local helpers ----------------------- */

const Stat: React.FC<{ icon: React.ComponentType<any>; label: string }> = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
    <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.7} />
    {label}
  </span>
);

// Isolated clock — re-renders only itself every second instead of the whole landing.
const LiveClock: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const iso = now.toISOString();
  return (
    <>
      <span className="hidden sm:inline opacity-60">{iso.slice(0, 10)}</span>
      <span className="text-border hidden sm:inline">|</span>
      <span className="tabular-nums text-foreground/80">{iso.slice(11, 19)} UTC</span>
    </>
  );
};

export default Index;

/* =============== Tool box primitives (landing-only) =============== */

type AccentKey = "primary" | "cyan" | "amber";

const accentMap: Record<
  AccentKey,
  { text: string; bg: string; border: string; rail: string; soft: string }
> = {
  primary: {
    text: "text-primary",
    bg: "bg-primary/10",
    border: "border-primary/40",
    rail: "bg-primary",
    soft: "bg-primary/5",
  },
  cyan: {
    text: "text-signal-cyan",
    bg: "bg-signal-cyan/10",
    border: "border-signal-cyan/40",
    rail: "bg-signal-cyan",
    soft: "bg-signal-cyan/5",
  },
  amber: {
    text: "text-signal-amber",
    bg: "bg-signal-amber/10",
    border: "border-signal-amber/40",
    rail: "bg-signal-amber",
    soft: "bg-signal-amber/5",
  },
};

const statusToneMap: Record<"ok" | "warn" | "info", string> = {
  ok: "bg-signal-green text-surface-0",
  warn: "bg-signal-amber text-surface-0",
  info: "bg-primary text-white",
};

const ToolBox: React.FC<{
  index: number;
  code: string;
  name: string;
  desc: string;
  Icon: React.ComponentType<any>;
  accent: AccentKey;
  status: string;
  statusTone: "ok" | "warn" | "info";
  tags: string[];
}> = ({ index, code, name, desc, Icon, accent, status, statusTone, tags }) => {
  const a = accentMap[accent];
  return (
    <div className="relative h-full bg-surface-2 overflow-hidden group/tool transition-colors hover:bg-surface-3/60">
      {/* Left vertical accent rail */}
      <span className={cn("absolute left-0 top-0 bottom-0 w-[3px]", a.rail, "opacity-60 group-hover/tool:opacity-100 transition-opacity")} />
      {/* Sweep on hover */}
      <span className={cn("absolute inset-x-0 bottom-0 h-px", a.rail, "scale-x-0 group-hover/tool:scale-x-100 origin-left transition-transform duration-500")} />
      {/* Big outline number */}
      <span className="pointer-events-none select-none absolute -right-2 -top-3 font-display text-[88px] leading-none text-foreground/[0.04] tracking-tighter">
        {String(index).padStart(2, "0")}
      </span>

      <div className="relative p-5 sm:p-6 flex flex-col h-full">
        {/* Header row: code + status pill */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className={cn("inline-block w-1.5 h-1.5 rounded-full", a.rail, "intel-pulse-red")} />
            {code}
          </div>
          <span className={cn(
            "font-mono text-[8.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 leading-none",
            statusToneMap[statusTone],
          )}>
            {status}
          </span>
        </div>

        {/* Big icon block — angled clip */}
        <div className={cn(
          "relative w-[58px] h-[58px] flex items-center justify-center mb-5 border",
          a.border, a.bg, "clip-corner-mark",
          "group-hover/tool:scale-[1.04] transition-transform duration-300",
        )}>
          <Icon className={cn("w-7 h-7", a.text)} strokeWidth={1.5} />
          {/* Diagonal cross-marker */}
          <span className={cn("absolute -bottom-1 -right-1 w-2 h-2", a.rail)} />
        </div>

        <h3 className="font-display text-[17px] font-bold tracking-wide leading-tight mb-1.5">
          {name}
        </h3>
        <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-4">{desc}</p>

        <div className="flex flex-wrap gap-1 mb-4">
          {tags.map((tag) => (
            <span
              key={tag}
              className="font-mono text-[8.5px] uppercase tracking-[0.12em] text-muted-foreground border border-border bg-surface-0 px-1.5 py-0.5"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-auto pt-3 border-t border-border flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em]">
          <span className="flex items-center gap-1.5 text-muted-foreground/80">
            <Activity className="w-3 h-3" /> &lt; 800ms
          </span>
          <span className={cn("flex items-center gap-1 transition-all duration-300", a.text, "translate-x-0 group-hover/tool:translate-x-1")}>
            EJECUTAR <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};

const UtilityBox: React.FC<{
  index: number;
  code: string;
  name: string;
  desc: string;
  Icon: React.ComponentType<any>;
  accent: AccentKey;
}> = ({ index, code, name, desc, Icon, accent }) => {
  const a = accentMap[accent];
  return (
    <div className="relative h-full bg-surface-2 overflow-hidden group/util hover:bg-surface-3/70 transition-colors">
      <span className={cn("absolute inset-x-0 top-0 h-[2px]", a.rail, "scale-x-0 group-hover/util:scale-x-100 origin-left transition-transform duration-400")} />
      <span className="pointer-events-none absolute -bottom-6 -right-2 font-display text-[110px] leading-none text-foreground/[0.04]">
        {String(index).padStart(2, "0")}
      </span>
      <div className="relative p-5 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground">{code}</div>
          <span className={cn("inline-block w-1.5 h-1.5 rounded-full", a.rail, "intel-pulse-red")} />
        </div>
        <div className={cn(
          "w-12 h-12 mb-4 flex items-center justify-center border",
          a.border, a.bg,
          "group-hover/util:rotate-[-4deg] transition-transform duration-300",
        )}>
          <Icon className={cn("w-6 h-6", a.text)} strokeWidth={1.5} />
        </div>
        <h4 className="font-display text-[15px] font-bold tracking-wide leading-tight">{name}</h4>
        <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed flex-1">{desc}</p>
        <div className="mt-4 pt-3 border-t border-border flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70">
          <span>ABRIR</span>
          <span className={cn("flex items-center gap-1 transition-all", a.text, "opacity-60 group-hover/util:opacity-100 group-hover/util:translate-x-1")}>
            <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};